import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { TimePicker12h } from '@/components/ui/time-picker-12h'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  User,
  Settings,
  LogOut,
  ChevronDown,
  Crown,
  Building2,
  Bell,
  Shield,
  Key,
  Palette,
  Save,
  AlertCircle,
  CheckCircle,
  Phone,
  Copy,
  MessageCircle,
  Clock
} from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useTheme } from '@/context/ThemeContext'
import { useNotifications } from '@/context/NotificationContext'
import { useRestaurant } from '@/context/RestaurantContext'
import { formatDate, getDaysRemaining } from '@/lib/utils'
import { ApiService } from '@/services/api'
import { DEFAULT_BUSINESS_HOURS } from '@/lib/businessDay'

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/

export default function UserMenu() {
  const { user, logout, updateUser } = useAuth()
  const { theme, setTheme, actualTheme } = useTheme()
  const { isSupported, isSubscribed, permission, toggleSubscription, sendTestNotification } = useNotifications()
  const { currentRestaurant, refreshRestaurants } = useRestaurant()
  const [isProfileOpen, setIsProfileOpen] = useState(false)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [showContactDialog, setShowContactDialog] = useState(false)
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [copiedNumber, setCopiedNumber] = useState(false)
  const SUPPORT_NUMBER = '+91 88916 32568'

  const [businessHoursForm, setBusinessHoursForm] = useState(() => currentRestaurant?.businessHours || DEFAULT_BUSINESS_HOURS)
  const [businessHoursSaveStatus, setBusinessHoursSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [businessHoursError, setBusinessHoursError] = useState<string>('')

  // Reseed the form from the current restaurant whenever the dialog is (re)opened,
  // so it always reflects the latest saved value without clobbering in-progress edits.
  // Deliberately keyed only on isSettingsOpen (not currentRestaurant?.businessHours) —
  // a successful save calls refreshRestaurants(), which changes that value while the
  // dialog is still open, and re-seeding on that would immediately reset the "saved"
  // status back to "idle" before the user ever sees the confirmation.
  useEffect(() => {
    if (isSettingsOpen) {
      setBusinessHoursForm(currentRestaurant?.businessHours || DEFAULT_BUSINESS_HOURS)
      setBusinessHoursSaveStatus('idle')
      setBusinessHoursError('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSettingsOpen])

  const handleBusinessHoursSave = async () => {
    if (!currentRestaurant?._id) return
    try {
      setBusinessHoursSaveStatus('saving')
      setBusinessHoursError('')

      if (!TIME_RE.test(businessHoursForm.openTime) || !TIME_RE.test(businessHoursForm.closeTime)) {
        setBusinessHoursError('Enter valid opening and closing times')
        setBusinessHoursSaveStatus('error')
        setTimeout(() => setBusinessHoursSaveStatus('idle'), 3000)
        return
      }

      if (businessHoursForm.openTime === businessHoursForm.closeTime) {
        setBusinessHoursError('Opening time and closing time cannot be the same')
        setBusinessHoursSaveStatus('error')
        setTimeout(() => setBusinessHoursSaveStatus('idle'), 3000)
        return
      }

      const response = await ApiService.updateBusinessHours(currentRestaurant._id, businessHoursForm)
      if (response.success) {
        setBusinessHoursSaveStatus('saved')
        await refreshRestaurants()
      } else {
        setBusinessHoursError(response.message || 'Failed to update business hours')
        setBusinessHoursSaveStatus('error')
      }
      setTimeout(() => setBusinessHoursSaveStatus('idle'), 3000)
    } catch (error: any) {
      setBusinessHoursError(error.response?.data?.message || 'Failed to update business hours')
      setBusinessHoursSaveStatus('error')
      setTimeout(() => setBusinessHoursSaveStatus('idle'), 3000)
    }
  }

  if (!user) return null

  const handleCopyNumber = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_NUMBER)
      setCopiedNumber(true)
      setTimeout(() => setCopiedNumber(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const handleCallSupport = () => {
    window.location.href = `tel:${SUPPORT_NUMBER}`
  }

  const handleWhatsAppSupport = () => {
    const phoneNumber = SUPPORT_NUMBER.replace(/[^0-9]/g, '')
    window.open(`https://wa.me/${phoneNumber}`, '_blank')
  }

  const handleProfileSave = async () => {
    try {
      setSaveStatus('saving')
      setErrorMessage('')
      
      // Basic validation
      if (!profileData.name.trim()) {
        setErrorMessage('Name is required')
        setSaveStatus('error')
        setTimeout(() => setSaveStatus('idle'), 3000)
        return
      }
      
      if (!profileData.email.trim()) {
        setErrorMessage('Email is required')
        setSaveStatus('error')
        setTimeout(() => setSaveStatus('idle'), 3000)
        return
      }
      
      const updateData = {
        name: profileData.name.trim(),
        email: profileData.email.trim(),
        phone: profileData.phone.trim()
      }
      
      const response = await ApiService.updateUser(user._id, updateData)
      if (response.success) {
        // Update user context with new data
        updateUser(updateData)
        setSaveStatus('saved')
        setErrorMessage('')
      } else {
        setErrorMessage(response.message || 'Failed to update profile')
        setSaveStatus('error')
      }
      setTimeout(() => setSaveStatus('idle'), 3000)
    } catch (error: any) {
      setErrorMessage(error.response?.data?.message || 'Failed to update profile')
      setSaveStatus('error')
      setTimeout(() => setSaveStatus('idle'), 3000)
    }
  }

  const handlePasswordChange = async () => {
    try {
      setSaveStatus('saving')
      setErrorMessage('')
      
      // Validation
      if (!profileData.currentPassword) {
        setErrorMessage('Current password is required')
        setSaveStatus('error')
        setTimeout(() => setSaveStatus('idle'), 3000)
        return
      }
      
      if (!profileData.newPassword) {
        setErrorMessage('New password is required')
        setSaveStatus('error')
        setTimeout(() => setSaveStatus('idle'), 3000)
        return
      }
      
      if (profileData.newPassword.length < 6) {
        setErrorMessage('Password must be at least 6 characters long')
        setSaveStatus('error')
        setTimeout(() => setSaveStatus('idle'), 3000)
        return
      }
      
      if (profileData.newPassword !== profileData.confirmPassword) {
        setErrorMessage('New passwords do not match')
        setSaveStatus('error')
        setTimeout(() => setSaveStatus('idle'), 3000)
        return
      }
      
      const passwordData = {
        currentPassword: profileData.currentPassword,
        newPassword: profileData.newPassword
      }
      
      const response = await ApiService.changePassword(user._id, passwordData)
      if (response.success) {
        setProfileData({
          ...profileData,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        })
        setSaveStatus('saved')
        setErrorMessage('')
      } else {
        setErrorMessage(response.message || 'Failed to update password')
        setSaveStatus('error')
      }
      setTimeout(() => setSaveStatus('idle'), 3000)
    } catch (error: any) {
      setErrorMessage(error.response?.data?.message || 'Failed to update password')
      setSaveStatus('error')
      setTimeout(() => setSaveStatus('idle'), 3000)
    }
  }

  const getRoleBadge = () => {
    if (user.userType === 'admin') {
      return (
        <Badge className="bg-purple-500 hover:bg-purple-600">
          <Crown className="w-3 h-3 mr-1" />
          Admin
        </Badge>
      )
    } else {
      return (
        <Badge className="bg-blue-500 hover:bg-blue-600">
          <Building2 className="w-3 h-3 mr-1" />
          Restaurant Owner
        </Badge>
      )
    }
  }

  const getSubscriptionStatus = () => {
    if (user.userType === 'admin') return null
    
    // Get subscription data from user context
    if (!user.subscriptionEndDate) return null
    
    const daysRemaining = getDaysRemaining(new Date(user.subscriptionEndDate))
    const subscriptionStatus = user.subscriptionStatus || 'active'
    
    if (subscriptionStatus === 'expired' || daysRemaining <= 0) {
      return (
        <Badge variant="destructive">
          <AlertCircle className="w-3 h-3 mr-1" />
          Expired
        </Badge>
      )
    } else if (subscriptionStatus === 'suspended') {
      return (
        <Badge variant="secondary">
          <AlertCircle className="w-3 h-3 mr-1" />
          Suspended
        </Badge>
      )
    } else if (daysRemaining <= 30) {
      return (
        <Badge className="bg-orange-500 hover:bg-orange-600">
          <AlertCircle className="w-3 h-3 mr-1" />
          Expires in {daysRemaining} days
        </Badge>
      )
    } else {
      return (
        <Badge className="bg-green-500 hover:bg-green-600">
          <CheckCircle className="w-3 h-3 mr-1" />
          Active ({daysRemaining} days)
        </Badge>
      )
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 h-auto py-2">
            <div className="w-8 h-8 sm:w-9 sm:h-9 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-semibold shadow-sm">
              {user.name.charAt(0).toUpperCase()}
            </div>
            <div className="hidden lg:flex flex-col text-left min-w-0">
              <p className="text-sm font-medium truncate max-w-[120px] leading-tight">{user.name}</p>
              <p className="text-xs text-muted-foreground truncate leading-tight">{user.userType === 'admin' ? 'Administrator' : 'Restaurant Owner'}</p>
            </div>
            <ChevronDown className="w-4 h-4 hidden sm:block flex-shrink-0" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-72 sm:w-80 max-w-[calc(100vw-2rem)]">
          <DropdownMenuLabel className="pb-3">
            <div className="flex flex-col gap-2">
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-base truncate">{user.name}</p>
                <p className="text-sm text-muted-foreground font-normal truncate">{user.email}</p>
              </div>
              <div className="flex-shrink-0">
                {getRoleBadge()}
              </div>
            </div>
            {getSubscriptionStatus() && (
              <div className="mt-3 pt-3 border-t">
                {getSubscriptionStatus()}
              </div>
            )}
          </DropdownMenuLabel>

          <DropdownMenuSeparator />

          <DropdownMenuItem onClick={() => setIsProfileOpen(true)} className="cursor-pointer py-2.5">
            <User className="w-4 h-4 mr-3 flex-shrink-0" />
            <span className="text-sm">Profile Settings</span>
          </DropdownMenuItem>

          <DropdownMenuItem onClick={() => setIsSettingsOpen(true)} className="cursor-pointer py-2.5">
            <Settings className="w-4 h-4 mr-3 flex-shrink-0" />
            <span className="text-sm">App Settings</span>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem onClick={logout} className="text-red-600 cursor-pointer py-2.5 font-medium">
            <LogOut className="w-4 h-4 mr-3 flex-shrink-0" />
            <span className="text-sm">Sign Out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Profile Settings Dialog */}
      <Dialog open={isProfileOpen} onOpenChange={setIsProfileOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">Profile Settings</DialogTitle>
            <DialogDescription className="text-sm">
              Manage your account information and security settings
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="profile" className="w-full">
            <TabsList className={`grid w-full ${user.userType === 'restaurant_owner' ? 'grid-cols-3' : 'grid-cols-2'}`}>
              <TabsTrigger value="profile" className="text-xs sm:text-sm">
                <span className="hidden sm:inline">Profile Information</span>
                <span className="sm:hidden">Profile</span>
              </TabsTrigger>
              <TabsTrigger value="security" className="text-xs sm:text-sm">Security</TabsTrigger>
              {user.userType === 'restaurant_owner' && (
                <TabsTrigger value="subscription" className="text-xs sm:text-sm">
                  <span className="hidden sm:inline">Subscription</span>
                  <span className="sm:hidden">Plan</span>
                </TabsTrigger>
              )}
            </TabsList>
            
            <TabsContent value="profile" className="space-y-3 sm:space-y-4 mt-4">
              <Card>
                <CardHeader className="pb-3 sm:pb-6">
                  <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                    <User className="w-4 h-4 sm:w-5 sm:h-5" />
                    Personal Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div className="sm:col-span-2 md:col-span-1">
                      <Label htmlFor="name" className="text-sm">Full Name</Label>
                      <Input
                        id="name"
                        value={profileData.name}
                        onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                        className="mt-1.5"
                      />
                    </div>

                    <div className="sm:col-span-2 md:col-span-1">
                      <Label htmlFor="email" className="text-sm">Email Address</Label>
                      <Input
                        id="email"
                        type="email"
                        value={profileData.email}
                        onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                        className="mt-1.5"
                      />
                    </div>

                    <div className="sm:col-span-2 md:col-span-1">
                      <Label htmlFor="phone" className="text-sm">Phone Number</Label>
                      <Input
                        id="phone"
                        value={profileData.phone}
                        onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                        className="mt-1.5"
                      />
                    </div>

                    <div className="sm:col-span-2 md:col-span-1">
                      <Label className="text-sm">Account Type</Label>
                      <div className="mt-2">
                        {getRoleBadge()}
                      </div>
                    </div>
                  </div>

                  {user.userType === 'restaurant_owner' && (
                    <div className="pt-3 sm:pt-4 border-t">
                      <h4 className="font-medium mb-2 text-sm sm:text-base">Restaurant Information</h4>
                      <div className="text-xs sm:text-sm text-muted-foreground space-y-1">
                        <p className="break-all"><strong>Restaurant ID:</strong> {user.restaurantId}</p>
                        <p className="break-all"><strong>Database:</strong> {user.databaseName}</p>
                        <p><strong>Member Since:</strong> {formatDate(user.createdAt)}</p>
                      </div>
                    </div>
                  )}

                  {saveStatus !== 'idle' && (
                    <Alert variant={saveStatus === 'error' ? 'destructive' : 'default'}>
                      {saveStatus === 'saving' && <Settings className="h-4 w-4 animate-spin" />}
                      {saveStatus === 'saved' && <CheckCircle className="h-4 w-4" />}
                      {saveStatus === 'error' && <AlertCircle className="h-4 w-4" />}
                      <AlertDescription>
                        {saveStatus === 'saving' && 'Saving changes...'}
                        {saveStatus === 'saved' && 'Profile updated successfully!'}
                        {saveStatus === 'error' && (errorMessage || 'Failed to update profile. Please try again.')}
                      </AlertDescription>
                    </Alert>
                  )}

                  <Button onClick={handleProfileSave} className="w-full sm:text-base" disabled={saveStatus === 'saving'}>
                    <Save className="w-4 h-4 mr-2" />
                    {saveStatus === 'saving' ? 'Saving...' : 'Save Changes'}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="security" className="space-y-3 sm:space-y-4 mt-4">
              <Card>
                <CardHeader className="pb-3 sm:pb-6">
                  <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                    <Key className="w-4 h-4 sm:w-5 sm:h-5" />
                    Change Password
                  </CardTitle>
                  <CardDescription className="text-xs sm:text-sm">
                    Update your password to keep your account secure
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 sm:space-y-4">
                  <div>
                    <Label htmlFor="currentPassword" className="text-sm">Current Password</Label>
                    <Input
                      id="currentPassword"
                      type="password"
                      value={profileData.currentPassword}
                      onChange={(e) => setProfileData({...profileData, currentPassword: e.target.value})}
                      className="mt-1.5"
                      placeholder="Enter current password"
                    />
                  </div>

                  <div>
                    <Label htmlFor="newPassword" className="text-sm">New Password</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      value={profileData.newPassword}
                      onChange={(e) => setProfileData({...profileData, newPassword: e.target.value})}
                      className="mt-1.5"
                      placeholder="Enter new password"
                    />
                  </div>

                  <div>
                    <Label htmlFor="confirmPassword" className="text-sm">Confirm New Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={profileData.confirmPassword}
                      onChange={(e) => setProfileData({...profileData, confirmPassword: e.target.value})}
                      className="mt-1.5"
                      placeholder="Confirm new password"
                    />
                  </div>

                  {saveStatus !== 'idle' && (
                    <Alert variant={saveStatus === 'error' ? 'destructive' : 'default'}>
                      {saveStatus === 'saving' && <Settings className="h-4 w-4 animate-spin" />}
                      {saveStatus === 'saved' && <CheckCircle className="h-4 w-4" />}
                      {saveStatus === 'error' && <AlertCircle className="h-4 w-4" />}
                      <AlertDescription>
                        {saveStatus === 'saving' && 'Updating password...'}
                        {saveStatus === 'saved' && 'Password updated successfully!'}
                        {saveStatus === 'error' && (errorMessage || 'Failed to update password. Please try again.')}
                      </AlertDescription>
                    </Alert>
                  )}

                  <Button
                    onClick={handlePasswordChange}
                    className="w-full sm:text-base"
                    disabled={saveStatus === 'saving' || !profileData.currentPassword || !profileData.newPassword}
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    {saveStatus === 'saving' ? 'Updating...' : 'Update Password'}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
            
            {user.userType === 'restaurant_owner' && (
              <TabsContent value="subscription" className="space-y-3 sm:space-y-4 mt-4">
                <Card>
                  <CardHeader className="pb-3 sm:pb-6">
                    <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                      <Crown className="w-4 h-4 sm:w-5 sm:h-5" />
                      Subscription Details
                    </CardTitle>
                    <CardDescription className="text-xs sm:text-sm">
                      Your current subscription plan and billing information
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <Label className="text-sm">Current Status</Label>
                        <div className="mt-2">
                          {getSubscriptionStatus()}
                        </div>
                      </div>

                      <div>
                        <Label className="text-sm">Plan Type</Label>
                        <p className="text-sm font-medium mt-1.5">Standard Plan</p>
                      </div>

                      {user.subscriptionStartDate && (
                        <div>
                          <Label className="text-sm">Start Date</Label>
                          <p className="text-xs sm:text-sm text-muted-foreground mt-1.5">
                            {formatDate(user.subscriptionStartDate)}
                          </p>
                        </div>
                      )}

                      {user.subscriptionEndDate && (
                        <div>
                          <Label className="text-sm">End Date</Label>
                          <p className="text-xs sm:text-sm text-muted-foreground mt-1.5">
                            {formatDate(user.subscriptionEndDate)}
                          </p>
                        </div>
                      )}

                      <div>
                        <Label className="text-sm">Payment Status</Label>
                        <div className="mt-1.5">
                          <Badge variant={user.paymentStatus === 'paid' ? 'default' : 'destructive'}>
                            {user.paymentStatus || 'Unknown'}
                          </Badge>
                        </div>
                      </div>

                      <div>
                        <Label className="text-sm">Restaurant ID</Label>
                        <p className="text-xs sm:text-sm text-muted-foreground mt-1.5 font-mono break-all">
                          {user.restaurantId}
                        </p>
                      </div>
                    </div>

                    {user.subscriptionEndDate && (
                      <div className="pt-4 border-t">
                        <div className="bg-muted/50 p-4 rounded-lg">
                          <h4 className="font-medium mb-2 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" />
                            Subscription Summary
                          </h4>
                          <div className="text-sm text-muted-foreground space-y-1">
                            {(() => {
                              const daysRemaining = getDaysRemaining(new Date(user.subscriptionEndDate))
                              const subscriptionStatus = user.subscriptionStatus || 'active'
                              
                              if (subscriptionStatus === 'expired' || daysRemaining <= 0) {
                                return (
                                  <div className="text-red-600">
                                    <p><strong>Status:</strong> Your subscription has expired</p>
                                    <p><strong>Action Required:</strong> Contact your administrator to renew your subscription</p>
                                  </div>
                                )
                              } else if (daysRemaining <= 7) {
                                return (
                                  <div className="text-orange-600">
                                    <p><strong>Warning:</strong> Your subscription expires in {daysRemaining} days</p>
                                    <p><strong>Recommendation:</strong> Contact your administrator to renew before expiration</p>
                                  </div>
                                )
                              } else if (daysRemaining <= 30) {
                                return (
                                  <div className="text-yellow-600">
                                    <p><strong>Notice:</strong> Your subscription expires in {daysRemaining} days</p>
                                    <p><strong>Tip:</strong> Consider renewing soon to avoid service interruption</p>
                                  </div>
                                )
                              } else {
                                return (
                                  <div className="text-green-600">
                                    <p><strong>Status:</strong> Your subscription is active and healthy</p>
                                    <p><strong>Next Review:</strong> {daysRemaining} days remaining</p>
                                  </div>
                                )
                              }
                            })()}
                          </div>
                        </div>
                      </div>
                    )}

                    {user.subscriptionStatus !== 'active' && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          Your subscription is not active. Some features may be limited. 
                          Please contact your administrator for assistance.
                        </AlertDescription>
                      </Alert>
                    )}

                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        variant="outline"
                        className="flex-1 text-sm"
                        onClick={() => setShowContactDialog(true)}
                      >
                        <Building2 className="w-4 h-4 mr-2" />
                        <span className="hidden sm:inline">Contact Support</span>
                        <span className="sm:hidden">Contact</span>
                      </Button>
                      <Button variant="outline" className="flex-1 text-sm">
                        <AlertCircle className="w-4 h-4 mr-2" />
                        <span className="hidden sm:inline">View Usage</span>
                        <span className="sm:hidden">Usage</span>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            )}
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* App Settings Dialog */}
      <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">App Settings</DialogTitle>
            <DialogDescription className="text-sm">
              Customize your application preferences
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 sm:space-y-6">
            <Card>
              <CardHeader className="pb-3 sm:pb-6">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <Palette className="w-4 h-4 sm:w-5 sm:h-5" />
                  Theme Preferences
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  Choose how the application looks to you
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  <Button
                    variant={theme === 'light' ? 'default' : 'outline'}
                    onClick={() => setTheme('light')}
                    className="flex flex-col gap-1 sm:gap-1.5 h-auto py-2.5 sm:py-3"
                  >
                    <div className="w-3 h-3 sm:w-4 sm:h-4 bg-white border-2 border-gray-300 rounded"></div>
                    <span className="text-[10px] sm:text-xs">Light</span>
                  </Button>

                  <Button
                    variant={theme === 'dark' ? 'default' : 'outline'}
                    onClick={() => setTheme('dark')}
                    className="flex flex-col gap-1 sm:gap-1.5 h-auto py-2.5 sm:py-3"
                  >
                    <div className="w-3 h-3 sm:w-4 sm:h-4 bg-gray-800 border-2 border-gray-600 rounded"></div>
                    <span className="text-[10px] sm:text-xs">Dark</span>
                  </Button>

                  <Button
                    variant={theme === 'system' ? 'default' : 'outline'}
                    onClick={() => setTheme('system')}
                    className="flex flex-col gap-1 sm:gap-1.5 h-auto py-2.5 sm:py-3"
                  >
                    <div className="w-3 h-3 sm:w-4 sm:h-4 bg-gradient-to-r from-white to-gray-800 border-2 border-gray-400 rounded"></div>
                    <span className="text-[10px] sm:text-xs">System</span>
                  </Button>
                </div>

                <p className="text-[10px] sm:text-xs text-muted-foreground mt-2 sm:mt-3">
                  Current theme: <span className="font-medium capitalize">{actualTheme}</span> mode
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3 sm:pb-6">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <Bell className="w-4 h-4 sm:w-5 sm:h-5" />
                  Notifications
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  Configure when you receive notifications
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 sm:space-y-4">
                  {/* Push Notifications */}
                  <div className="p-3 sm:p-4 border rounded-lg">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <Label className="font-medium text-sm">Push Notifications</Label>
                        <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">
                          {!isSupported ? 'Not supported in this browser' :
                           permission === 'denied' ? 'Permission denied' :
                           isSubscribed ? 'Enabled - You\'ll receive notifications' : 'Disabled'}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={toggleSubscription}
                        disabled={!isSupported || permission === 'denied'}
                        className="text-xs flex-shrink-0"
                      >
                        {isSubscribed ? 'Disable' : 'Enable'}
                      </Button>
                    </div>

                    {isSubscribed && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={sendTestNotification}
                        className="w-full mt-2 text-xs"
                      >
                        Send Test Notification
                      </Button>
                    )}
                  </div>

                  {/* Other notification settings */}
                  <div className="space-y-2.5 sm:space-y-3">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="subscription-alerts" className="text-xs sm:text-sm">Subscription Alerts</Label>
                      <input type="checkbox" id="subscription-alerts" defaultChecked className="toggle h-4 w-4" />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="system-updates" className="text-xs sm:text-sm">System Updates</Label>
                      <input type="checkbox" id="system-updates" defaultChecked className="toggle h-4 w-4" />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="email-notifications" className="text-xs sm:text-sm">Email Notifications</Label>
                      <input type="checkbox" id="email-notifications" className="toggle h-4 w-4" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {user.userType === 'restaurant_owner' && currentRestaurant && (
              <Card>
                <CardHeader className="pb-3 sm:pb-6">
                  <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
                    Business Hours
                  </CardTitle>
                  <CardDescription className="text-xs sm:text-sm">
                    Set your restaurant's opening and closing time. All reports use this window
                    to define "one day" — set a closing time past midnight if you trade overnight.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 sm:space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label htmlFor="business-open-time" className="text-xs sm:text-sm">Opening time</Label>
                        <TimePicker12h
                          id="business-open-time"
                          value={businessHoursForm.openTime}
                          onChange={(v) => setBusinessHoursForm(prev => ({ ...prev, openTime: v }))}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="business-close-time" className="text-xs sm:text-sm">Closing time</Label>
                        <TimePicker12h
                          id="business-close-time"
                          value={businessHoursForm.closeTime}
                          onChange={(v) => setBusinessHoursForm(prev => ({ ...prev, closeTime: v }))}
                        />
                      </div>
                    </div>

                    {businessHoursSaveStatus !== 'idle' && (
                      <Alert variant={businessHoursSaveStatus === 'error' ? 'destructive' : 'default'}>
                        {businessHoursSaveStatus === 'saving' && <Settings className="h-4 w-4 animate-spin" />}
                        {businessHoursSaveStatus === 'saved' && <CheckCircle className="h-4 w-4" />}
                        {businessHoursSaveStatus === 'error' && <AlertCircle className="h-4 w-4" />}
                        <AlertDescription className="text-xs sm:text-sm">
                          {businessHoursSaveStatus === 'saving' && 'Saving business hours...'}
                          {businessHoursSaveStatus === 'saved' && 'Business hours updated successfully!'}
                          {businessHoursSaveStatus === 'error' && (businessHoursError || 'Failed to update business hours. Please try again.')}
                        </AlertDescription>
                      </Alert>
                    )}

                    <Button
                      onClick={handleBusinessHoursSave}
                      className="w-full"
                      disabled={businessHoursSaveStatus === 'saving'}
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {businessHoursSaveStatus === 'saving' ? 'Saving...' : 'Save Business Hours'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Contact Support Dialog */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-full">
                <Phone className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              ordore Customer Support
            </DialogTitle>
            <DialogDescription className="text-sm">
              Get help with your subscription and technical support
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Support Number Display */}
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200 dark:border-blue-800">
              <CardContent className="pt-6">
                <div className="text-center space-y-3">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-600 rounded-full">
                    <Phone className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-muted-foreground mb-1">24/7 Support Hotline</p>
                    <p className="text-2xl sm:text-3xl font-bold text-blue-600 dark:text-blue-400 tracking-wide">
                      {SUPPORT_NUMBER}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyNumber}
                    className="w-full sm:w-auto"
                  >
                    {copiedNumber ? (
                      <>
                        <CheckCircle className="mr-2 h-4 w-4 text-green-600" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="mr-2 h-4 w-4" />
                        Copy Number
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Support Options */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Contact Methods:</p>

              <Button
                onClick={handleCallSupport}
                className="w-full justify-start bg-green-600 hover:bg-green-700 text-white"
              >
                <Phone className="mr-3 h-5 w-5" />
                <div className="text-left">
                  <div className="font-semibold">Call Now</div>
                  <div className="text-xs opacity-90">Direct phone call</div>
                </div>
              </Button>

              <Button
                onClick={handleWhatsAppSupport}
                className="w-full justify-start bg-[#25D366] hover:bg-[#20BD5A] text-white"
              >
                <MessageCircle className="mr-3 h-5 w-5" />
                <div className="text-left">
                  <div className="font-semibold">WhatsApp</div>
                  <div className="text-xs opacity-90">Chat on WhatsApp</div>
                </div>
              </Button>
            </div>

            {/* Support Info */}
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs sm:text-sm">
                Our support team is available 24/7 to help with subscription renewals, technical issues, and account management.
              </AlertDescription>
            </Alert>
          </div>

          <div className="text-center">
            <p className="text-xs text-muted-foreground">
              Average response time: Less than 5 minutes
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}